
import React, { useContext, useState, useMemo, useCallback } from 'react';
import { PageTitle, Card, EmptyState, Button } from '../components/common';
import { AuthContext } from '../contexts/AuthContext';
import { mockCourses } from '../mockData';
import { Course, CourseSection, QuizQuestion } from '../types';

const ArrowRightIcon = () => <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5-5 5M6 12h12" /></svg>;
const ClockIcon = () => <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
const CheckCircleIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;


interface Quiz {
    course: Course;
    section: CourseSection;
    quiz: NonNullable<CourseSection['quiz']>;
}

interface QuizTakerProps {
    quiz: Quiz;
    onBack: () => void;
}

const QuizTaker: React.FC<QuizTakerProps> = ({ quiz, onBack }) => {
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState<Record<string, string>>({});
    const [timeLeft, setTimeLeft] = useState(3 * 60); // 3 minutes for the quiz
    const [isSubmitted, setIsSubmitted] = useState(false);

    const handleSubmit = useCallback(() => {
        setIsSubmitted(true);
    }, []);

    React.useEffect(() => {
        if(isSubmitted) return;
        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    handleSubmit();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [isSubmitted, handleSubmit]);

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const currentQuestion = quiz.quiz.questions[currentQuestionIndex];
    const totalQuestions = quiz.quiz.questions.length;

    const handleAnswerSelect = (option: string) => {
        setAnswers(prev => ({ ...prev, [currentQuestion.id]: option }));
    };

    const handleNext = () => {
        if (currentQuestionIndex < totalQuestions - 1) {
            setCurrentQuestionIndex(prev => prev + 1);
        } else {
            handleSubmit();
        }
    };

    const score = useMemo(() => {
        if (!isSubmitted) return 0;
        return quiz.quiz.questions.reduce((acc, q) => acc + (answers[q.id] === q.correctAnswer ? 1 : 0), 0);
    }, [isSubmitted, answers, quiz.quiz.questions]);

    if (isSubmitted) {
        return (
            <Card className="p-8 text-center flex flex-col items-center">
                <CheckCircleIcon />
                <h2 className="text-2xl font-bold mt-4">Quiz Submitted!</h2>
                <p className="text-gray-600 mt-2">You have completed the quiz for {quiz.course.title}.</p>
                <p className="text-4xl font-bold my-4 text-primary">{score} <span className="text-2xl text-gray-800">/ {totalQuestions}</span></p>
                <Button onClick={onBack}>Back to Quiz List</Button>
            </Card>
        );
    }

    return (
        <Card>
            <div className="p-4 border-b flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <img src={quiz.course.imageUrl} alt={quiz.course.title} className="w-10 h-10 rounded-md object-cover"/>
                    <div>
                        <h2 className="font-bold text-gray-800">{quiz.course.title}</h2>
                        <p className="text-sm text-gray-500">{quiz.section.title}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600 font-medium">
                    <ClockIcon />
                    <span>{formatTime(timeLeft)} / 03:00</span>
                </div>
            </div>
            <div className="p-6">
                 <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold text-gray-800">Quiz Progress</h3>
                    <span className="text-sm text-gray-600">Question {currentQuestionIndex + 1} out of {totalQuestions}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5 mb-6">
                    <div className="bg-green-500 h-2.5 rounded-full" style={{ width: `${((currentQuestionIndex + 1) / totalQuestions) * 100}%` }}></div>
                </div>

                <h4 className="text-lg font-bold text-gray-900 mb-4">{currentQuestion.question}</h4>
                <div className="space-y-3">
                    {currentQuestion.options.map(option => (
                        <label key={option} className={`flex items-center p-4 border rounded-lg cursor-pointer transition-all ${answers[currentQuestion.id] === option ? 'bg-primary-50 border-primary' : 'border-gray-200'}`}>
                            <input
                                type="radio"
                                name={currentQuestion.id}
                                value={option}
                                checked={answers[currentQuestion.id] === option}
                                onChange={() => handleAnswerSelect(option)}
                                className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                            />
                            <span className="ml-3 text-gray-700">{option}</span>
                        </label>
                    ))}
                </div>
            </div>
            <div className="p-4 border-t flex justify-end">
                <Button onClick={handleNext} disabled={!answers[currentQuestion.id]} className="bg-pink-500 hover:bg-pink-600 text-white">
                    {currentQuestionIndex < totalQuestions - 1 ? 'Next' : 'Submit'}
                </Button>
            </div>
        </Card>
    );
};


const QuizAttemptsPage: React.FC = () => {
    const auth = useContext(AuthContext);
    const [selectedQuiz, setSelectedQuiz] = useState<Quiz | null>(null);

    const availableQuizzes = useMemo(() => {
        if (!auth?.currentUser) return [];
        const userCourses = mockCourses.filter(c => auth?.currentUser?.enrolledCourseIds.includes(c.id));
        
        const quizzes: Quiz[] = [];
        userCourses.forEach(course => {
            (course.sections || []).forEach(section => {
                if (section.quiz && section.quiz.questions.length > 0) {
                    quizzes.push({
                        course,
                        section,
                        quiz: section.quiz
                    });
                }
            });
        });
        return quizzes;
    }, [auth?.currentUser]);

    if (selectedQuiz) {
        return <QuizTaker quiz={selectedQuiz} onBack={() => setSelectedQuiz(null)} />;
    }

    return (
        <div>
            <PageTitle title="My Quiz Attempts" />

            {availableQuizzes.length > 0 ? (
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {availableQuizzes.map((quizItem, index) => (
                         <Card key={`${quizItem.course.id}-${quizItem.section.id}`} className="p-4 hover:shadow-lg transition-shadow">
                            <button onClick={() => setSelectedQuiz(quizItem)} className="w-full flex justify-between items-center text-left">
                                <div>
                                    <h3 className="font-bold text-gray-800">{quizItem.course.title}</h3>
                                    <p className="text-sm text-gray-500 mt-1">Number of Questions : {quizItem.quiz.questions.length.toString().padStart(2, '0')}</p>
                                </div>
                                <ArrowRightIcon />
                            </button>
                        </Card>
                    ))}
                </div>
            ) : (
                <EmptyState
                    title="No Quizzes Available"
                    message="Quizzes from your enrolled courses will appear here."
                />
            )}
        </div>
    );
};

export default QuizAttemptsPage;
